'use client'

export type CaseStatus = "live" | "dev" | "mvp" | "growing" | "design" | "concept";

export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  live: "Запущено",
  dev: "В работе",
  mvp: "MVP",
  growing: "Развивается",
  design: "Проектируется",
  concept: "Концепция",
};

/** Тип бизнеса — первый уровень фильтра */
export type BusinessType =
  | "Услуги"
  | "Торговля"
  | "B2B"
  | "Мероприятия"
  | "Образование"
  | "Сообщества"
  | "Другие";

/** Задача — второй уровень фильтра */
export type CaseTask =
  | "Приложение"
  | "Сайт"
  | "CRM"
  | "Продажи"
  | "E-commerce"
  | "Лояльность"
  | "Автоматизация"
  | "Мероприятия"
  | "AI"
  | "Нетворкинг";

export interface FlowStep {
  label: string;
  note?: string;
}

/** Параллельные линии сценария: клиент и бизнес */
export interface CaseFlow {
  title: string;
  intro?: string;
  client: FlowStep[];
  business: FlowStep[];
}

export interface FeatureGroup {
  title: string;
  items: string[];
}

export interface IntegrationLink {
  name: string;
  payload: string;
  status: "live" | "dev" | "soon";
}

export interface AutomationChain {
  trigger: string;
  steps: string[];
}

export interface CaseStudy {
  slug: string;
  client: string;
  plan: "PRO" | "PRO+";
  appUrl: string;
  websiteUrl: string;
  qrImage: string;
  /** короткое имя для инициалов/маркера */
  marker: string;
  category: string;
  businessType: BusinessType;
  tasks: CaseTask[];
  status: CaseStatus;
  year: string;
  /** одна строка задачи для карточки */
  shortDescription: string;
  /** главный тезис кейса */
  heroStatement: string;
  heroSubtitle: string;
  /** что было до платформы */
  challenge: string[];
  challengeIntro: string;
  /** продуктовые задачи */
  goals: string[];
  flow: CaseFlow;
  clientSideFeatures: string[];
  businessSideFeatures: string[];
  integrations: IntegrationLink[];
  automations: AutomationChain[];
  /** карточка клиента «один клиент — одна история» */
  customerProfile?: { name: string; items: string[] };
  before: string[];
  after: string[];
  /** только подтверждённые метрики; пусто — блок не показываем */
  verifiedMetrics: { value: string; label: string }[];
  businessValue: string[];
  quote?: { text: string; author: string; role: string };
  roadmap?: { now: string[]; next: string[] };
  relatedCases: string[];
}

export const CASES: CaseStudy[] = [
  {
    slug: "private-clinic",
    client: "Частная клиника",
    plan: "PRO",
    appUrl: "",
    websiteUrl: "",
    qrImage: "",
    marker: "КЛ",
    category: "Медицина / услуги",
    businessType: "Услуги",
    tasks: ["Приложение", "Сайт", "CRM", "Лояльность", "Автоматизация"],
    status: "dev",
    year: "2026",
    shortDescription: "Из записи в мессенджере и Excel — в приложение, сайт и единую работу с пациентом.",
    heroStatement: "Цифровой путь пациента — от записи до повторного визита.",
    heroSubtitle: "Приложение, сайт, CRM, личный кабинет и коммуникации в единой системе.",
    challengeIntro:
      "Клиника выросла, и прежний процесс перестал в неё помещаться: запись жила в разных каналах, а история пациента — в разных местах.",
    challenge: [
      "Запись по телефону и в мессенджерах",
      "Часть данных ведётся в таблицах",
      "У пациента нет личного кабинета",
      "Напоминания о визите отправляются вручную",
      "Нет собственной программы лояльности",
      "Результаты и переписка хранятся отдельно от карточки пациента",
    ],
    goals: [
      "Упростить запись и собрать её в один канал",
      "Дать пациенту личный кабинет с историей и документами",
      "Объединить обращения из всех каналов с CRM",
      "Автоматизировать напоминания и сервисные сообщения",
      "Создать причину возвращаться в клинику",
    ],
    flow: {
      title: "Как работает сценарий",
      intro: "Слева — то, что видит пациент. Справа — что в этот момент происходит внутри клиники.",
      client: [
        { label: "Открывает приложение или сайт" },
        { label: "Выбирает услугу и врача" },
        { label: "Записывается на приём" },
        { label: "Получает напоминание", note: "push / сообщение" },
        { label: "Приходит на приём" },
        { label: "Видит результаты в кабинете" },
        { label: "Получает персональное предложение" },
        { label: "Записывается повторно" },
      ],
      business: [
        { label: "Обращение попадает в CRM" },
        { label: "Заявка привязана к карточке пациента" },
        { label: "Запись видна в расписании" },
        { label: "Задача администратору" },
        { label: "Визит отмечен, история пополнена" },
        { label: "Документы прикреплены к профилю" },
        { label: "Сегмент и бонусы обновлены" },
        { label: "Повторный визит — в том же профиле" },
      ],
    },
    clientSideFeatures: [
      "Приложение клиники",
      "Сайт с админ-частью",
      "Онлайн-запись",
      "Услуги и врачи",
      "Личный кабинет",
      "Чат с клиникой",
      "Документы и результаты",
      "Программа лояльности",
    ],
    businessSideFeatures: [
      "CRM и карточка пациента",
      "Заявки и обращения",
      "Расписание и записи",
      "Задачи администраторам",
      "Роли и права доступа",
      "Автоматические коммуникации",
      "Аналитика по записям",
    ],
    integrations: [
      { name: "Платёжная система", payload: "оплата услуг и предоплата записи", status: "dev" },
      { name: "Лабораторный сервис", payload: "передача результатов исследований в кабинет", status: "soon" },
    ],
    automations: [
      { trigger: "Новая запись", steps: ["Заявка в CRM", "Задача администратору", "Подтверждение пациенту"] },
      { trigger: "За сутки до приёма", steps: ["Напоминание пациенту", "Отметка в расписании"] },
      { trigger: "После визита", steps: ["Начисление бонусов", "Обновление сегмента", "Предложение по профилю"] },
    ],
    customerProfile: {
      name: "Пациент",
      items: ["Заявка", "Чат", "Записи", "Визиты", "Документы", "Бонусы", "История"],
    },
    before: [
      "Запись в нескольких каналах",
      "Часть данных в таблицах",
      "Отдельные переписки",
      "Нет кабинета пациента",
      "Ручные напоминания",
    ],
    after: [
      "Единая онлайн-запись",
      "CRM с историей пациента",
      "Личный кабинет и документы",
      "Автоматические коммуникации",
      "Связанный путь пациента",
    ],
    verifiedMetrics: [],
    businessValue: [
      "Собственный прямой канал к пациенту",
      "Единый рабочий контекст для администраторов и врачей",
      "Основа для повторных визитов и сервиса",
    ],
    roadmap: {
      now: ["Приложение", "Сайт", "Запись", "CRM", "Личный кабинет"],
      next: ["Результаты лабораторий", "Программы наблюдения", "Расширенная аналитика"],
    },
    relatedCases: ["groove", "groverman"],
  },
  {
    slug: "piter",
    client: "Издательский дом «Питер»",
    plan: "PRO",
    appUrl: "",
    websiteUrl: "",
    qrImage: "",
    marker: "ПТ",
    category: "Издательство / e-commerce",
    businessType: "Торговля",
    tasks: ["Приложение", "Сайт", "E-commerce", "CRM", "Продажи"],
    status: "mvp",
    year: "2026",
    shortDescription: "Из каталога книг — в собственную цифровую среду издательства.",
    heroStatement: "Не интернет-магазин книг, а цифровая среда издательства.",
    heroSubtitle: "Каталог, покупка, заказы и CRM в одном контуре — с интеграцией 1С и платёжной системой.",
    challengeIntro:
      "Издательство работает с большим каталогом и разными аудиториями: читателями, авторами и партнёрами. Продажи и учёт жили отдельно от коммуникаций с читателем.",
    challenge: [
      "Каталог и учёт ведутся в 1С, а онлайн-продажи — отдельно",
      "Нет единого профиля читателя",
      "История покупок не связана с коммуникациями",
      "Нет собственного канала к читателю",
      "Авторы и партнёры работают вне цифрового контура",
    ],
    goals: [
      "Собрать каталог и покупку в одном приложении и на сайте",
      "Связать заказы с CRM и учётом",
      "Дать читателю профиль с историей покупок",
      "Подготовить основу для подписки и цифрового чтения",
    ],
    flow: {
      title: "Как работает сценарий",
      intro: "Основной сценарий MVP: читатель → каталог → книга → покупка → заказ → CRM.",
      client: [
        { label: "Заходит в приложение или на сайт" },
        { label: "Смотрит каталог" },
        { label: "Открывает карточку книги" },
        { label: "Оплачивает заказ" },
        { label: "Получает статус доставки" },
        { label: "Видит историю покупок" },
      ],
      business: [
        { label: "Профиль читателя создан" },
        { label: "Каталог синхронизирован с 1С" },
        { label: "Наличие и цена актуальны" },
        { label: "Заказ и оплата в CRM" },
        { label: "Задача на обработку заказа" },
        { label: "История покупок в профиле" },
      ],
    },
    clientSideFeatures: [
      "Приложение издательства",
      "Сайт с админ-частью",
      "Каталог книг",
      "Корзина и оплата",
      "Личный кабинет читателя",
      "Статусы заказов",
    ],
    businessSideFeatures: [
      "CRM и профиль читателя",
      "Заказы и сделки",
      "Задачи на обработку",
      "Роли сотрудников",
      "Аналитика продаж",
    ],
    integrations: [
      { name: "1С", payload: "каталог, номенклатура, наличие, заказы", status: "dev" },
      { name: "Платёжная система", payload: "оплата заказов", status: "dev" },
    ],
    automations: [
      { trigger: "Оплаченный заказ", steps: ["Сделка в CRM", "Задача на сборку", "Уведомление читателю"] },
      { trigger: "Изменение статуса", steps: ["Обновление в кабинете", "Push читателю"] },
    ],
    customerProfile: {
      name: "Читатель",
      items: ["Профиль", "Заказы", "Оплаты", "Избранное", "Коммуникации"],
    },
    before: [
      "Каталог и учёт отдельно от онлайн-продаж",
      "Нет профиля читателя",
      "Коммуникации не связаны с покупками",
    ],
    after: [
      "Каталог и покупка в приложении и на сайте",
      "Заказы в CRM",
      "Профиль читателя с историей",
      "Связь с учётной системой",
    ],
    verifiedMetrics: [],
    businessValue: [
      "Прямой канал к читателю без посредников",
      "Продажи и учёт в одном процессе",
      "Площадка, которая может расти в подписку и цифровое чтение",
    ],
    roadmap: {
      now: ["Каталог", "Покупка", "Заказы", "1С", "Оплата"],
      next: ["Подписка", "Читалка", "Аудио", "События и авторы", "Кабинет автора"],
    },
    relatedCases: ["groverman", "private-clinic"],
  },
  {
    slug: "groove",
    client: "Поместье «Грув»",
    plan: "PRO+",
    appUrl: "",
    websiteUrl: "",
    qrImage: "",
    marker: "ГР",
    category: "Hospitality / мероприятия",
    businessType: "Услуги",
    tasks: ["Приложение", "Сайт", "Мероприятия", "Продажи", "AI"],
    status: "growing",
    year: "2026",
    shortDescription: "Разные услуги одного комплекса — один профиль клиента и одна система.",
    heroStatement: "Мероприятия, проживание, SPA и ферма — в одном клиентском профиле.",
    heroSubtitle: "Комплекс с несколькими направлениями работает как единая цифровая среда гостя.",
    challengeIntro:
      "У комплекса несколько направлений, и каждое собирало заявки по-своему. Гость одного направления оставался неизвестным для остальных.",
    challenge: [
      "Мероприятия, проживание, SPA и ферма работают как отдельные направления",
      "Заявки приходят в разные каналы",
      "Нет единого профиля гостя",
      "Пакеты и календарь загрузки ведутся вручную",
      "Ивент-агентства работают через переписку",
    ],
    goals: [
      "Собрать все направления в одном приложении и на сайте",
      "Создать единый профиль гостя",
      "Показать календарь и пакеты услуг онлайн",
      "Помочь гостю подобрать формат отдыха",
      "Дать ивент-агентствам собственный кабинет",
    ],
    flow: {
      title: "Как работает сценарий",
      intro: "Гость приходит за одним направлением, а остаётся в общей среде комплекса.",
      client: [
        { label: "Проходит AI-квиз подбора" },
        { label: "Видит пакеты и календарь" },
        { label: "Оставляет заявку или бронирует" },
        { label: "Получает подтверждение" },
        { label: "Приезжает и пользуется услугами" },
        { label: "Приглашает друзей по реферальной ссылке" },
      ],
      business: [
        { label: "Ответы квиза в профиле гостя" },
        { label: "Доступность и загрузка в системе" },
        { label: "Заявка и сделка в CRM" },
        { label: "Задача менеджеру направления" },
        { label: "История услуг в одном профиле" },
        { label: "Реферальные начисления учтены" },
      ],
    },
    clientSideFeatures: [
      "Приложение комплекса",
      "Сайт с админ-частью",
      "Мероприятия и календарь",
      "Проживание, SPA, ферма",
      "Пакеты услуг",
      "AI-квиз подбора",
      "Реферальная система",
    ],
    businessSideFeatures: [
      "CRM и единый профиль гостя",
      "Заявки и сделки по направлениям",
      "Календарь загрузки",
      "Кабинет ивент-агентства",
      "Роли сотрудников направлений",
      "Задачи и автоматизация",
    ],
    integrations: [{ name: "Платёжная система", payload: "оплата пакетов и брони", status: "dev" }],
    automations: [
      { trigger: "Заявка с квиза", steps: ["Профиль с предпочтениями", "Сделка в CRM", "Задача менеджеру"] },
      { trigger: "Приглашение друга", steps: ["Реферальная привязка", "Начисление", "Уведомление гостю"] },
    ],
    customerProfile: {
      name: "Гость",
      items: ["Квиз", "Заявки", "Брони", "Мероприятия", "SPA", "Рефералы", "История"],
    },
    before: [
      "Направления живут отдельно",
      "Заявки в разных каналах",
      "Гость каждый раз новый",
      "Календарь и пакеты вручную",
    ],
    after: [
      "Все направления в одном приложении",
      "Единый профиль гостя",
      "Онлайн-календарь и пакеты",
      "Кабинет партнёра-агентства",
      "Реферальные механики",
    ],
    verifiedMetrics: [],
    businessValue: [
      "Гость одного направления становится клиентом всего комплекса",
      "Продажи и загрузка видны в одном месте",
      "Партнёры и агентства работают внутри системы",
    ],
    roadmap: {
      now: ["Мероприятия", "Проживание", "SPA", "Календарь", "Пакеты"],
      next: ["Расширенный кабинет агентства", "Программа лояльности", "Развитие AI-подбора"],
    },
    relatedCases: ["business-forum", "private-clinic"],
  },
  {
    slug: "groverman",
    client: "«Груверман»",
    plan: "PRO+",
    appUrl: "",
    websiteUrl: "",
    qrImage: "",
    marker: "ГВ",
    category: "Ферма / e-commerce",
    businessType: "Торговля",
    tasks: ["Приложение", "Сайт", "E-commerce", "Лояльность", "AI"],
    status: "growing",
    year: "2026",
    shortDescription: "Продукт, заказ, доставка и бонусы — в одном канале повторных покупок.",
    heroStatement: "От разовой покупки продуктов — к постоянному клиенту фермы.",
    heroSubtitle: "Каталог, доставка по районам, бонусы, уровни скидок и AI-рецепты в одном приложении.",
    challengeIntro:
      "Ферма продаёт продукты с регулярным спросом, но повторная покупка зависела от того, вспомнит ли клиент о ней сам.",
    challenge: [
      "Заказы принимаются в мессенджерах",
      "Районы и условия доставки объясняются вручную",
      "Нет системы бонусов и скидок",
      "Нет причины вернуться за повторной покупкой",
      "Клиентская база не собрана в одном месте",
    ],
    goals: [
      "Перенести заказ в приложение и на сайт",
      "Настроить районы доставки и условия",
      "Ввести бонусы и уровни скидки",
      "Дать клиенту повод возвращаться",
      "Собрать клиентскую базу в CRM",
    ],
    flow: {
      title: "Как работает сценарий",
      intro: "Продукт → заказ → доставка → бонусы → рецепт → повторная покупка.",
      client: [
        { label: "Выбирает продукты в каталоге" },
        { label: "Оформляет заказ с доставкой" },
        { label: "Получает заказ" },
        { label: "Получает бонусы" },
        { label: "Видит AI-рецепт под покупку" },
        { label: "Заказывает снова с уровнем скидки" },
      ],
      business: [
        { label: "Профиль клиента в CRM" },
        { label: "Заказ и район доставки" },
        { label: "Задача на сборку и доставку" },
        { label: "Начисление бонусов" },
        { label: "Сегмент обновлён" },
        { label: "Повторные заказы в истории клиента" },
      ],
    },
    clientSideFeatures: [
      "Приложение фермы",
      "Сайт с админ-частью",
      "Каталог продуктов",
      "Районы доставки",
      "Бонусы и уровни скидки",
      "Реферальная система",
      "AI-рецепты",
      "Чат с фермой",
    ],
    businessSideFeatures: [
      "CRM и база клиентов",
      "Заказы и сделки",
      "Задачи на сборку и доставку",
      "Правила бонусов и сегментов",
      "Автоматизация коммуникаций",
    ],
    integrations: [{ name: "Платёжная система", payload: "оплата заказов", status: "dev" }],
    automations: [
      { trigger: "Покупка", steps: ["Начисление баллов", "Обновление уровня скидки", "Персональное предложение"] },
      { trigger: "Новый заказ", steps: ["Сделка в CRM", "Задача на сборку", "Уведомление о доставке"] },
    ],
    customerProfile: {
      name: "Покупатель",
      items: ["Заказы", "Доставка", "Баллы", "Уровень скидки", "Рефералы", "Чат"],
    },
    before: ["Заказы в переписке", "Нет бонусов", "Нет базы клиентов", "Повторная покупка случайна"],
    after: [
      "Заказ в приложении и на сайте",
      "Бонусы и уровни скидки",
      "CRM с историей заказов",
      "Механики возврата клиента",
    ],
    verifiedMetrics: [],
    businessValue: [
      "Собственный канал продаж вместо переписки",
      "Повторные покупки встроены в процесс",
      "Клиентская база принадлежит бизнесу",
    ],
    roadmap: {
      now: ["Каталог", "Заказы", "Доставка", "Бонусы", "Рефералы"],
      next: ["Подписка на регулярную доставку", "Расширение AI-рекомендаций", "Партнёрские продукты"],
    },
    relatedCases: ["piter", "dobrodomik"],
  },
  {
    slug: "dobrodomik",
    client: "Добродомик",
    plan: "PRO",
    appUrl: "",
    websiteUrl: "",
    qrImage: "",
    marker: "ДД",
    category: "Благотворительность / клуб",
    businessType: "Сообщества",
    tasks: ["Приложение", "CRM", "Лояльность", "Автоматизация", "Нетворкинг"],
    status: "dev",
    year: "2026",
    shortDescription: "Два сценария в одной системе: пользователь-донор и партнёр проекта.",
    heroStatement: "Клуб пользователей и партнёров с разными ролями в одной системе.",
    heroSubtitle: "Донаты, история помощи, баллы, кабинет партнёра, закрытые чаты и документы.",
    challengeIntro:
      "У проекта две разные аудитории с разными потребностями: люди, которые помогают, и партнёры, которые поддерживают проект системно.",
    challenge: [
      "Донаты и поддержка учитываются вне единой системы",
      "У человека нет истории своей помощи",
      "Партнёры общаются через переписку и почту",
      "Нет закрытого пространства для партнёров",
      "Партнёрские пользы не собраны в одном месте",
    ],
    goals: [
      "Показать человеку его историю помощи",
      "Ввести баллы и приглашения",
      "Создать кабинет партнёра с документами и чатами",
      "Собрать партнёрские предложения в одном каталоге",
      "Свести обе роли к одной CRM",
    ],
    flow: {
      title: "Как работают два сценария",
      intro: "Одна система обслуживает пользователя и партнёра с разными правами и интерфейсами.",
      client: [
        { label: "Делает донат" },
        { label: "Видит историю своей помощи" },
        { label: "Получает баллы" },
        { label: "Приглашает друзей" },
        { label: "Пользуется партнёрскими предложениями" },
      ],
      business: [
        { label: "Партнёр оформляет регулярную поддержку" },
        { label: "Получает кабинет партнёра" },
        { label: "Закрытые чаты и документы" },
        { label: "Размещает партнёрские предложения" },
        { label: "Всё видно в CRM проекта" },
      ],
    },
    clientSideFeatures: [
      "Приложение проекта",
      "Донаты и история помощи",
      "Баллы",
      "Приглашения и рефералы",
      "Каталог партнёрских польз",
      "Квиз «Стать партнёром»",
    ],
    businessSideFeatures: [
      "CRM пользователей и партнёров",
      "Роли и права доступа",
      "Кабинет партнёра",
      "Закрытые чаты",
      "Документы партнёров",
      "Балльная система и правила",
    ],
    integrations: [{ name: "Платёжная система", payload: "донаты и регулярные платежи", status: "dev" }],
    automations: [
      { trigger: "Донат", steps: ["Запись в историю помощи", "Начисление баллов", "Благодарность пользователю"] },
      { trigger: "Заявка с квиза «Стать партнёром»", steps: ["Заявка в CRM", "Задача менеджеру", "Доступ в кабинет"] },
    ],
    customerProfile: {
      name: "Участник клуба",
      items: ["Донаты", "История помощи", "Баллы", "Приглашения", "Партнёрские пользы"],
    },
    before: ["Поддержка учитывается вручную", "Нет истории помощи", "Партнёры вне системы", "Пользы разрозненны"],
    after: [
      "История помощи в профиле",
      "Баллы и приглашения",
      "Кабинет партнёра с чатами и документами",
      "Единая CRM для двух ролей",
    ],
    verifiedMetrics: [],
    businessValue: [
      "Люди видят результат своей помощи и остаются в проекте",
      "Партнёры получают закрытое пространство и понятную ценность",
      "Проект управляет двумя аудиториями из одной системы",
    ],
    roadmap: {
      now: ["Донаты", "Баллы", "Кабинет партнёра", "CRM"],
      next: ["Расширение партнёрского каталога", "Публичная отчётность", "Развитие клубных механик"],
    },
    relatedCases: ["business-forum", "groove"],
  },
  {
    slug: "business-forum",
    client: "Форум работающей молодёжи",
    plan: "PRO",
    appUrl: "",
    websiteUrl: "",
    qrImage: "",
    marker: "ФР",
    category: "Мероприятия / сообщество",
    businessType: "Мероприятия",
    tasks: ["Приложение", "Мероприятия", "Нетворкинг", "Автоматизация"],
    status: "live",
    year: "2025",
    shortDescription: "Регистрация, программа, QR-вход и нетворкинг в приложении мероприятия.",
    heroStatement: "Цифровая среда участника: до, во время и после мероприятия.",
    heroSubtitle: "Регистрация, программа, спикеры, визитки, QR-вход, чаты и активности в одном приложении.",
    challengeIntro:
      "У крупного мероприятия много участников и плотная программа. После завершения форума аудитория обычно расходится и теряется.",
    challenge: [
      "Регистрация и списки участников ведутся отдельно",
      "Программа меняется, а печатные материалы — нет",
      "Знакомства зависят от случайности",
      "Вопросы спикерам собираются вручную",
      "После мероприятия аудитория теряется",
    ],
    goals: [
      "Собрать регистрацию и вход в одном приложении",
      "Дать участникам актуальную программу и карту",
      "Сделать знакомства управляемыми",
      "Собрать вопросы и активности внутри среды",
      "Сохранить сообщество после мероприятия",
    ],
    flow: {
      title: "Как работает сценарий",
      intro: "До мероприятия — регистрация. На мероприятии — программа и знакомства. После — сообщество остаётся.",
      client: [
        { label: "Регистрируется" },
        { label: "Получает билет с QR" },
        { label: "Проходит по QR на входе" },
        { label: "Смотрит программу и спикеров" },
        { label: "Знакомится через цифровые визитки" },
        { label: "Задаёт вопросы и участвует в викторинах" },
        { label: "Остаётся в чатах после форума" },
      ],
      business: [
        { label: "Участник в базе мероприятия" },
        { label: "Билет привязан к профилю" },
        { label: "Вход отмечен, статистика собирается" },
        { label: "Программа обновляется из админ-части" },
        { label: "Нетворкинг виден организатору" },
        { label: "Вопросы модерируются" },
        { label: "Аудитория сохранена для следующих событий" },
      ],
    },
    clientSideFeatures: [
      "Приложение мероприятия",
      "Регистрация",
      "Билет и QR-вход",
      "Программа и спикеры",
      "Цифровые визитки",
      "Участники и нетворкинг",
      "Чаты",
      "Викторины и активности",
      "Карта площадки",
      "Вопросы спикерам",
    ],
    businessSideFeatures: [
      "База участников",
      "Управление программой",
      "Модерация вопросов",
      "Партнёры мероприятия",
      "Статистика посещения",
      "Push-рассылки участникам",
    ],
    integrations: [],
    automations: [
      { trigger: "Регистрация", steps: ["Участник в базе", "Билет с QR", "Push с деталями"] },
      { trigger: "Изменение программы", steps: ["Обновление в приложении", "Push участникам"] },
    ],
    customerProfile: {
      name: "Участник",
      items: ["Регистрация", "Билет", "Визитка", "Программа", "Знакомства", "Чаты"],
    },
    before: ["Списки и регистрация отдельно", "Печатная программа", "Случайный нетворкинг", "Аудитория теряется"],
    after: [
      "Регистрация и QR-вход в приложении",
      "Актуальная программа",
      "Управляемый нетворкинг",
      "Сообщество сохраняется после события",
    ],
    verifiedMetrics: [],
    businessValue: [
      "Организатор владеет аудиторией мероприятия",
      "Программа и коммуникации управляются в реальном времени",
      "Событие становится точкой входа в сообщество",
    ],
    roadmap: {
      now: ["Регистрация", "Программа", "QR", "Нетворкинг", "Чаты"],
      next: ["Серия мероприятий в одной среде", "Расширенная аналитика", "Партнёрские активности"],
    },
    relatedCases: ["dobrodomik", "groove"],
  },
];

export const BUSINESS_TYPES: (BusinessType | "Все")[] = [
  "Все",
  "Услуги",
  "Торговля",
  "B2B",
  "Мероприятия",
  "Образование",
  "Сообщества",
  "Другие",
];

export const CASE_TASKS: CaseTask[] = [
  "Приложение",
  "Сайт",
  "CRM",
  "Продажи",
  "E-commerce",
  "Лояльность",
  "Автоматизация",
  "Мероприятия",
  "AI",
  "Нетворкинг",
];

export const getCaseStudy = (slug?: string) => CASES.find((c) => c.slug === slug);

export const getRelated = (c: CaseStudy) =>
  c.relatedCases.map((s) => CASES.find((x) => x.slug === s)).filter(Boolean) as CaseStudy[];
